<template>
  <section class="cr-list bq-list" aria-label="Lead records">
    <div class="cr-list__head">
      <div
        class="cr-list__columns bq-list__columns"
        :style="{ gridTemplateColumns: gridColumns }"
        aria-hidden="true"
      >
        <span class="bq-list__select">
          <input
            type="checkbox"
            :checked="allSelected"
            aria-label="Select all leads"
            @change="$emit('toggle-select-all')"
          />
        </span>
        <span
          v-for="column in columns"
          :key="column.key"
          class="bq-list__field-head"
          :title="column.label"
        >
          {{ column.label }}
        </span>
        <span>Status</span>
        <span>Submitted</span>
        <span>Actions</span>
      </div>
    </div>

    <div class="cr-list__scroll">
      <div v-if="loading" class="cr-list__body">
        <div v-for="n in 8" :key="n" class="cr-row cr-row--skeleton" />
      </div>

      <div v-else-if="queries.length" class="cr-list__body" role="list">
        <BusinessQueriesRow
          v-for="query in queries"
          :key="query.id"
          :query="query"
          :columns="columns"
          :grid-columns="gridColumns"
          :status-options="statusOptionsFor ? statusOptionsFor(query) : statusOptions"
          :selected="selectedIds.includes(query.requestId)"
          @toggle-select="$emit('toggle-select', $event)"
          @update-status="$emit('update-status', $event)"
          @view-inquiry="$emit('view-inquiry', $event)"
          @contact-lead="$emit('contact-lead', $event)"
          @edit="$emit('edit', $event)"
          @enable="$emit('enable', $event)"
          @disable="$emit('disable', $event)"
          @delete="$emit('delete', $event)"
        />
      </div>

      <div v-else-if="isSearchActive" class="cr-empty">
        <v-icon icon="mdi-magnify-close" size="40" class="cr-empty__icon" />
        <p class="cr-empty__title">No matching leads</p>
        <p class="cr-empty__subtitle">Try another search or clear the app bar filter.</p>
      </div>

      <div v-else class="cr-empty">
        <v-icon icon="mdi-message-text-outline" size="40" class="cr-empty__icon" />
        <p class="cr-empty__title">No leads yet</p>
        <p class="cr-empty__subtitle">
          Add a lead manually or wait for website inquiry submissions to appear here.
        </p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import BusinessQueriesRow from '@/components/BusinessQueries/BusinessQueriesRow.vue'
import { buildLeadsTableGridColumns } from '@/utils/leadFields'

defineOptions({ name: 'BusinessQueriesList' })

const props = defineProps({
  queries: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  isSearchActive: { type: Boolean, default: false },
  selectedIds: { type: Array, default: () => [] },
  /** Enabled field defs from the active context (table headers). */
  columns: { type: Array, default: () => [] },
  // Shared status options (active context) and an optional per-lead
  // resolver — statuses are scoped to each lead's own context.
  statusOptions: { type: Array, default: () => [] },
  statusOptionsFor: { type: Function, default: null },
})

defineEmits([
  'update-status',
  'view-inquiry',
  'contact-lead',
  'edit',
  'enable',
  'disable',
  'delete',
  'toggle-select',
  'toggle-select-all',
])

const gridColumns = computed(() => buildLeadsTableGridColumns(props.columns))

const allSelected = computed(
  () =>
    props.queries.length > 0 &&
    props.queries.every((query) => props.selectedIds.includes(query.requestId)),
)
</script>
